import { useIntl } from "react-intl";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { Stack } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import {
	ApplicationLogo,
	ApplicationIcon,
	Licensed,
	useAuthorizedValue,
	useFeatureFlaggedValue,
	useLicensedValue,
} from "@packsize/authentication";
import { PrimaryNavbar, type NavbarItem } from "@packsize/components";
import { useFeatureFlags } from "@packsize/feature-flags";

import { ErrorsAndWarningsModal } from "@/components/errors-and-warnings";
import {
	allAdministratorPermissions,
	allDIMPermissions,
	allOperatorPermissions,
	allReportingPermissions,
} from "@/views/configuration/roles/role-utils";
import { useHasDownstreamMachine } from "@/hooks/machines";

import { NavigationMenuItem } from "./components/navigation-menu-item";
import { TenantSelectDropDown } from "./tenant-select-dropdown";
import { UserMenu } from "./user-menu";

export const PacknetNavbar = () => {
	const intl = useIntl();
	const { pathname } = useLocation();
	const { isFeatureFlagEnabled } = useFeatureFlags();
	const displayNavbar = pathname.toLowerCase() !== "/site-select";
	const hasDownstreamMachine = useHasDownstreamMachine({ enabled: displayNavbar });

	const closingTabValue = useLicensedValue(
		useAuthorizedValue(
			{ link: "/closing", text: intl.formatMessage({ id: "Closing", defaultMessage: "Closing" }) },
			allOperatorPermissions.map((permission) => permission.name),
		),
		"Core",
		"basic",
		"ff-pack-net-production",
	);

	const navbarItems = [
		useLicensedValue(
			useAuthorizedValue(
				{ link: "/dashboard", text: intl.formatMessage({ id: "Dashboard", defaultMessage: "Dashboard" }) },
				"AccessDashboard",
			),
			"Core",
			"basic",
			"ff-pack-net-dashboard",
		),
		useLicensedValue(
			useAuthorizedValue(
				{ link: "/production", text: intl.formatMessage({ id: "Production", defaultMessage: "Production" }) },
				allOperatorPermissions.map((permission) => permission.name),
			),
			"Core",
			"basic",
			"ff-pack-net-production",
		),
		hasDownstreamMachine ? closingTabValue : null,
		useAuthorizedValue(
			{ link: "/configuration", text: intl.formatMessage({ id: "Configuration", defaultMessage: "Configuration" }) },
			allAdministratorPermissions.map((permission) => permission.name),
		),
		useLicensedValue(
			useAuthorizedValue(
				{ link: "/dimensioning", text: intl.formatMessage({ id: "DIM", defaultMessage: "DIM" }) },
				allDIMPermissions.map((permission) => permission.name),
			),
			"DIM",
			"essential",
		),
		useLicensedValue(
			useAuthorizedValue(
				{ link: "/reporting", text: intl.formatMessage({ id: "Report", defaultMessage: "Report" }) },
				allReportingPermissions.map((permission) => permission.name),
			),
			"Reporting",
			"basic",
			undefined,
			true,
			null,
			true,
		),
		useFeatureFlaggedValue(
			{ link: "/tools", text: intl.formatMessage({ id: "Tools", defaultMessage: "Tools" }) },
			"architecture-allow-packnet-tools",
		),
	].filter((tab): tab is NavbarItem => tab !== null);

	if (displayNavbar === false) {
		return null;
	}

	return (
		<PrimaryNavbar
			logo={<ApplicationLogo />}
			icon={<ApplicationIcon />}
			navbarItems={navbarItems}
			actions={
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="flex-end"
					spacing={2}
					sx={{ width: "100%", height: "100%", minWidth: 0, position: "relative", zIndex: 1 }}
				>
					<ErrorsAndWarningsModal />
					<TenantSelectDropDown />

					<Stack direction="row" height="100%">
						<Licensed productCategory="Core" tier="basic" licenseFeatureFlag="ff-pack-net-production">
							<NavigationMenuItem
								dark
								component={NavLink}
								to={isFeatureFlagEnabled("import-batch-import") ? "/search/v2/" : "/search/"}
								data-cy="search-button"
							>
								<SearchRounded />
							</NavigationMenuItem>
						</Licensed>

						<UserMenu />
					</Stack>
				</Stack>
			}
		/>
	);
};
